import React, { useState } from "react";
import {
    ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, ResponsiveContainer,
    Tooltip, Cell, ReferenceArea, ReferenceLine, LabelList,
} from "recharts";
import { MapPin, Plus, Check } from "lucide-react";
import FunderShell from "./FunderShell";
import { SectionCard, scoreColor } from "./FunderUI";
import { getWhiteSpace } from "../../service/FunderPortalService";

const INTERP = {
    "White space": { color: "var(--success)", bg: "var(--success-bg)" },
    Watch: { color: "var(--series-amber)", bg: "var(--warning-bg)" },
    "Well covered": { color: "var(--ink-450)", bg: "var(--surface-4)" },
};
const interpStyle = (i) => INTERP[i] || INTERP["Well covered"];

function WhiteSpaceTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px", fontSize: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
            <div style={{ fontWeight: 600, color: "var(--ink-900)", marginBottom: 2 }}>{d.name}</div>
            <div style={{ color: "var(--ink-450)" }}>Coverage {d.x}% · Frontier {d.y} · Momentum {d.momentum}</div>
        </div>
    );
}

function Inner({ callId }) {
    const rows = getWhiteSpace(callId);
    const [added, setAdded] = useState(() => new Set());

    const chartData = rows.map((r) => ({
        name: r.name,
        short: r.name.split(" ").slice(0, 3).join(" "),
        x: r.coverage,
        y: r.frontierScore,
        momentum: r.momentum,
        interp: r.interpretation,
    }));

    const sorted = [...rows].sort((a, b) => (b.frontierScore - b.coverage) - (a.frontierScore - a.coverage));

    const toggleMemo = (name) =>
        setAdded((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });

    return (
        <div className="funder-section-stack">
            <div className="funder-page-header">
                <div className="funder-overline">Strategic opportunity</div>
                <h1 className="funder-h1">White Space</h1>
                <p className="funder-subtitle">Where the research frontier is strong but funder coverage is thin — candidate areas to seed or expand in the next call.</p>
            </div>

            <SectionCard kicker="Coverage × Frontier" title="Where is the frontier under-funded?">
                <div className="funder-scatter-box" style={{ width: "100%", height: 340 }}>
                    <ResponsiveContainer>
                        <ScatterChart margin={{ top: 16, right: 28, bottom: 28, left: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                            <ReferenceArea x1={0} x2={40} y1={75} y2={100} fill="var(--success)" fillOpacity={0.07} stroke="var(--success)" strokeOpacity={0.3} strokeDasharray="3 3" />
                            <ReferenceLine x={40} stroke="var(--border)" />
                            <ReferenceLine y={75} stroke="var(--border)" />
                            <XAxis type="number" dataKey="x" domain={[0, 100]} tick={{ fontSize: 11, fill: "var(--ink-400)" }} label={{ value: "Funder coverage →", position: "insideBottom", offset: -14, fontSize: 11, fill: "var(--ink-400)" }} />
                            <YAxis type="number" dataKey="y" domain={[40, 100]} tick={{ fontSize: 11, fill: "var(--ink-400)" }} label={{ value: "Frontier strength", angle: -90, position: "insideLeft", fontSize: 11, fill: "var(--ink-400)" }} />
                            <ZAxis range={[110, 110]} />
                            <Tooltip content={<WhiteSpaceTooltip />} cursor={{ strokeDasharray: "3 3" }} />
                            <Scatter data={chartData}>
                                {chartData.map((d, i) => (
                                    <Cell key={i} fill={interpStyle(d.interp).color} fillOpacity={d.interp === "White space" ? 0.95 : 0.6} />
                                ))}
                                <LabelList dataKey="short" position="right" style={{ fill: "var(--ink-350)", fontSize: 10 }} />
                            </Scatter>
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
                <p style={{ fontSize: 11.5, color: "var(--ink-350)", marginTop: 8, marginBottom: 0, display: "flex", alignItems: "center", gap: 6 }}>
                    <MapPin size={13} /> Highlighted quadrant: high frontier strength, low funder coverage.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 12 }}>
                    {Object.entries(INTERP).map(([label, s]) => (
                        <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--ink-500)" }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />{label}
                        </span>
                    ))}
                </div>
            </SectionCard>

            <SectionCard kicker="Sorted by white-space opportunity" title="Cluster opportunity table">
                <div style={{ overflowX: "auto" }}>
                    <table className="funder-table">
                        <thead>
                            <tr>
                                <th>Cluster</th>
                                <th style={{ textAlign: "right" }}>Frontier</th>
                                <th style={{ textAlign: "right" }}>Coverage</th>
                                <th>Momentum</th>
                                <th style={{ textAlign: "right" }}>Novelty</th>
                                <th style={{ textAlign: "right" }}>Methods</th>
                                <th style={{ textAlign: "right" }}>Impact</th>
                                <th>Assessment</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((r) => {
                                const s = interpStyle(r.interpretation);
                                const isAdded = added.has(r.name);
                                return (
                                    <tr key={r.name}>
                                        <td style={{ minWidth: 240 }}>
                                            <div style={{ fontWeight: 600, color: "var(--ink-900)" }}>{r.name}</div>
                                            <div style={{ fontSize: 11.5, color: "var(--ink-350)", marginTop: 2 }}>Suggested: {r.question}</div>
                                        </td>
                                        <td style={{ textAlign: "right", fontWeight: 600 }}>{r.frontierScore}</td>
                                        <td style={{ textAlign: "right" }}>{r.coverage}%</td>
                                        <td style={{ minWidth: 90 }}>
                                            <div className="funder-bar-track" style={{ width: 70 }}>
                                                <div className="funder-bar-fill" style={{ width: `${r.momentum}%`, background: "var(--series-blue)" }} />
                                            </div>
                                        </td>
                                        <td style={{ textAlign: "right", color: scoreColor("novelty"), fontWeight: 600 }}>{r.avgNovelty}</td>
                                        <td style={{ textAlign: "right", color: scoreColor("methodology"), fontWeight: 600 }}>{r.avgMethodology}</td>
                                        <td style={{ textAlign: "right", color: scoreColor("impact"), fontWeight: 600 }}>{r.avgImpact}</td>
                                        <td><span className="status-badge" style={{ background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{r.interpretation}</span></td>
                                        <td>
                                            <button className="funder-btn-ghost" onClick={() => toggleMemo(r.name)} style={isAdded ? { borderColor: "var(--success)", color: "var(--success)" } : undefined}>
                                                {isAdded ? <><Check size={13} /> Added</> : <><Plus size={13} /> Memo</>}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p style={{ fontSize: 11.5, color: "var(--ink-350)", marginTop: 10, marginBottom: 0 }}>
                    "Add to Memo" is a demo action — it flags a cluster for a future strategy memo (no data is sent anywhere).
                </p>
            </SectionCard>
        </div>
    );
}

export default function FunderWhiteSpace() {
    return (
        <FunderShell activePage="White Space">
            {(callId, call) => <Inner callId={callId} call={call} />}
        </FunderShell>
    );
}
